/**
 * The e-mail TEMPLATES client — contract line #33 (ADDITIVE v1.9.28, T.Z130).
 * ZERO contract change here: T.Z134 only mounts the UI on the routes that
 * already exist (the shapes mirror `apps/api/src/mailing/email-templates.*.ts`
 * 1:1, the way `mailing-lists.ts` mirrors #34).
 *
 *   #33.1 GET   /api/email-templates                                 → `{data:{templates:[…]}}`  (bodies excluded)
 *   #33.2 POST  /api/email-templates                                 → `{data:{template}}`       (key + title + v1)
 *   #33.3 GET   /api/email-templates/:templateId/versions/:version    → `{data:{version}}`        (the FULL row, incl. bodyHtml)
 *   #33.4 POST  /api/email-templates/:templateId/versions             → `{data:{version}}`        (NEW version, number server-computed)
 *   #33.5 PATCH /api/email-templates/:templateId                      → `{data:{template}}`       (`{archived}` — the only off-switch)
 *
 * Everything is MANAGER+ (403 FORBIDDEN below — the nav hides the surface, the
 * same rule the rest of «Рассылки» follows). The error codes the page surfaces
 * verbatim (all NEW in v1.9.28):
 *   400 VALIDATION · 404 TEMPLATE_NOT_FOUND · 409 TEMPLATE_KEY_EXISTS ·
 *   409 TEMPLATE_VERSION_EXISTS (a lost create race) · 409 TEMPLATE_ARCHIVED.
 * Status codes follow the controller's own map: the two creates answer 201,
 * the archive patch 200.
 * THE ONE INVARIANT the UI must respect: a version row is IMMUTABLE once
 * created (the DB trigger refuses its update), so «save» is always #33.2/#33.4
 * — there is no PUT, and #33.3 answers are safe to cache.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { requestEnvelope } from './client';

export const EMAIL_TEMPLATES_ROOT = '/email-templates';

/** One documented variable, as the wire and the jsonb column both carry it. */
export interface EmailTemplateVariableDto {
  name: string;
  description: string | null;
}

/** A version WITHOUT its body — the list's shape (bodies are ~15 KB each; #33.3 carries one). */
export interface EmailTemplateVersionMetaDto {
  id: number;
  templateId: number;
  version: number;
  subject: string;
  changelogNote: string | null;
  variables: EmailTemplateVariableDto[];
  createdAt: string;
  createdByUserId: number | null;
}

/** A version WITH its body — #33.3's read and the two create answers. */
export interface EmailTemplateVersionFullDto extends EmailTemplateVersionMetaDto {
  bodyHtml: string;
}

/** T.Z210 (ADDITIVE v1.9.72) — the channel a template speaks. */
export type MailingChannel = 'EMAIL' | 'SMS';

/** One template (#33.1/#33.2/#33.5) — live first / archived last in #33.1's order. */
export interface EmailTemplateDto {
  id: number;
  key: string;
  /** T.Z210 — absent on an api older than v1.9.72 (an e-mail template). */
  channel?: MailingChannel;
  title: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdByUserId: number | null;
  /** The newest version number (null = no version yet — unreachable through this api). */
  latestVersion: number | null;
  /** The versions, OLDEST first, WITHOUT their bodies. */
  versions: EmailTemplateVersionMetaDto[];
}

// --------------------------------------------------------------- reads -----

/**
 * #33.1 — every template with its version METADATA, unpaginated (the #28
 * dictionary precedent: templates are a handful per campaign and a picker needs
 * them all). The picker in the list-create fold reads THIS hook too — one query
 * key, one cache, one refresh.
 */
export function useEmailTemplates() {
  return useQuery({
    queryKey: ['email-templates', 'index'],
    queryFn: ({ signal }) =>
      requestEnvelope<{ templates: EmailTemplateDto[] }>('GET', EMAIL_TEMPLATES_ROOT, ['data'], { signal }),
  });
}

/**
 * #33.3 — ONE version, WITH its body. Versions are immutable (the DB trigger
 * in migration 0034 refuses their update), so a cached body can never lie — a
 * modest `staleTime` keeps the editor's prefill fresh against a re-created
 * cache without re-reading on every modal open.
 *
 * THE PAYLOAD UNDER `data` IS `{ version: … }` — the api's own
 * `EmailTemplateVersionResponse` (#33.3), the same wrapper the two write
 * answers carry. T.Z142: the generic used to be the bare DTO, TS believed
 * `read.data.data.subject` existed, and the real api handed back the wrapper —
 * `undefined` reached the renderer and the SPA crashed on «Открыть». The shape
 * is pinned by `email-templates-wire.unit.spec.ts` and the e2e stub returns it.
 */
export function useTemplateVersion(templateId: number | null, version: number | null) {
  return useQuery({
    queryKey: ['email-templates', 'version', templateId, version],
    enabled: templateId !== null && version !== null,
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      requestEnvelope<{ version: EmailTemplateVersionFullDto }>(
        'GET',
        `${EMAIL_TEMPLATES_ROOT}/${templateId}/versions/${version}`,
        ['data'],
        { signal },
      ),
  });
}

// -------------------------------------------------------------- writes -----

/** The body of #33.2 — a template and its FIRST version in one call. */
export interface EmailTemplateCreateInput {
  key: string;
  title: string;
  /** T.Z210 — absent on the wire = EMAIL. An SMS template's `bodyHtml` carries
   * the PLAIN TEXT of the message; `subject` is its short display label. */
  channel?: MailingChannel;
  subject: string;
  bodyHtml: string;
  changelogNote?: string;
}

/** The body of #33.4 — a NEW version of an existing template. */
export interface EmailTemplateVersionCreateInput {
  subject: string;
  bodyHtml: string;
  changelogNote?: string;
}

/**
 * #33.2 — create a template together with its version 1. The variable NAMES
 * are SCANNED out of subject + bodyHtml by the api; the UI sends none (there is
 * no description field to fill — a documented note about a token the content
 * does not carry is a 400).
 */
export function useCreateTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: EmailTemplateCreateInput) =>
      requestEnvelope<{ template: EmailTemplateDto }>('POST', EMAIL_TEMPLATES_ROOT, ['data'], {
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['email-templates'] }),
  });
}

/**
 * #33.4 — the correction path: saving ALWAYS creates the next version (number
 * server-computed, max + 1); the old versions stay exactly as they were.
 */
export function useCreateTemplateVersion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, ...input }: EmailTemplateVersionCreateInput & { templateId: number }) =>
      requestEnvelope<{ version: EmailTemplateVersionFullDto }>(
        'POST',
        `${EMAIL_TEMPLATES_ROOT}/${templateId}/versions`,
        ['data'],
        { headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) },
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['email-templates'] }),
  });
}

/**
 * #33.5 — the archive toggle (there is NO delete). `archived: true` sinks the
 * template to the bottom of #33.1 and closes it to new versions (409
 * TEMPLATE_ARCHIVED); `false` brings it back. Existing versions — and any list
 * that pinned them — read exactly as before.
 */
export function useArchiveTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, archived }: { templateId: number; archived: boolean }) =>
      requestEnvelope<{ template: EmailTemplateDto }>('PATCH', `${EMAIL_TEMPLATES_ROOT}/${templateId}`, ['data'], {
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ archived }),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['email-templates'] }),
  });
}
